'use client';

/**
 * Renders a ticker logo from parqet.com's free CDN with persistent
 * "no logo here" caching so the same 404 doesn't spam the browser
 * console every time the user re-renders the screener / watchlist /
 * heatmap. The first request for a missing ticker still 404s (no way
 * to avoid that on a third-party CDN we don't pre-check), but the
 * ticker is then added to a localStorage-backed Set and skipped on
 * every subsequent render — across page navigations and reloads.
 *
 * Drop-in replacement for the four hand-rolled `<img src="parqet…">`
 * usages that existed before (CompactScreenerTable, WatchlistContent,
 * HeatmapView, stocks/[ticker]/page). Each still controls its own
 * className / size; this component just owns the URL + onError +
 * missing-cache plumbing.
 */

import React, { useState } from 'react';

const STORAGE_KEY = 'ticker-logo.missing.v1';

// Module-level Set so the missing-ticker cache is shared across every
// rendered <TickerLogo>. Lazy-loaded on first use because SSR doesn't
// have localStorage and importing this file at module-load would
// throw under Node.
let cache: Set<string> | null = null;
function loadCache(): Set<string> {
  if (cache) return cache;
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    cache = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    cache = new Set<string>();
  }
  return cache;
}
function persistCache(): void {
  if (typeof window === 'undefined' || !cache) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(cache)));
  } catch { /* quota exceeded or storage disabled — silently drop */ }
}

/** True when this ticker has already 404'd at least once. */
export function isLogoMissing(ticker: string): boolean {
  return loadCache().has(ticker.toUpperCase());
}

/** Mark a ticker as missing so future renders skip the request. */
export function markLogoMissing(ticker: string): void {
  const set = loadCache();
  const t = ticker.toUpperCase();
  if (set.has(t)) return;
  set.add(t);
  persistCache();
}

export interface TickerLogoProps {
  ticker: string;
  /** Pixel size. Sent as both width and height; the className still
   *  owns layout sizing (w-4 h-4 etc.) since callers have their own
   *  Tailwind sizing conventions. */
  size?: number;
  className?: string;
  /** Optional element rendered in place of the img when the ticker
   *  has no available logo (after the first 404 has been cached).
   *  Defaults to null → empty space matching the img's intrinsic
   *  flex behavior in the surrounding row. */
  fallback?: React.ReactNode;
  /** Loading priority. Defaults to 'lazy' — set 'eager' on above-the-
   *  fold renders like the stock detail header. */
  loading?: 'lazy' | 'eager';
}

export default function TickerLogo({
  ticker, size = 16, className, fallback = null, loading = 'lazy',
}: TickerLogoProps) {
  // Initialize state from the persisted cache so we don't briefly
  // render an <img> that we already know will 404.
  const [missing, setMissing] = useState(() => isLogoMissing(ticker));

  if (missing) return <>{fallback}</>;

  return (
    <img
      // Parqet keys logos with `-` instead of `.` so dotted tickers
      // like BRK.B resolve as BRK-B. Match the upstream convention or
      // every dotted ticker would 404 (and end up cached as missing).
      src={`https://assets.parqet.com/logos/symbol/${ticker.replace(/\./g, '-')}`}
      alt=""
      width={size}
      height={size}
      loading={loading}
      className={className}
      onError={() => {
        markLogoMissing(ticker);
        setMissing(true);
      }}
    />
  );
}
